/*
  AGNTC Phase 2 — Quick add for product cards.
  Paired with snippets/agntc-quick-add.liquid.

  Resolves a variant from the card's own embedded variant matrix and posts that
  explicit id to /cart/add.js. It deliberately never queries the page for variant
  inputs — reading them globally is what causes the theme's wrong-product-added
  bug. One delegated listener serves every card on the page.

  2026-08-06 — the trigger moved onto the product image and is revealed on hover
  (Astrid, 6 Aug: "can we have the buy now on hover over the image ... I find our
  current design feels heavy with the big large black buy button"). That is a
  pure layout change: the same [data-qa-open] button opens the same panel with the
  same option chips, so her other point — "You also CAN NOT add to bag with
  choosing color and size" — stays answered. Nothing about variant resolution or
  the add path changed. What DID change here: the magnifier's quick-view binding
  had to be taken over from the theme (see bindQuickView).
*/
(function () {
  'use strict';

  var MONEY = function (cents) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  function variantsOf(root) {
    if (root._agntcVariants) return root._agntcVariants;
    var tag = root.querySelector('[data-qa-variants]');
    var parsed = [];
    try { parsed = JSON.parse(tag.textContent); } catch (e) { parsed = []; }
    root._agntcVariants = parsed;
    return parsed;
  }

  function selection(root) {
    return Array.prototype.map.call(root.querySelectorAll('[data-qa-opt]'), function (group) {
      var on = group.querySelector('[data-qa-chip][aria-pressed="true"]');
      return on ? on.dataset.value : null;
    });
  }

  function match(root) {
    var sel = selection(root);

    /*
      REVIEW FIX 2026-08-06 — single-variant products were a dead end.

      When product.has_only_default_variant is true the snippet sets
      has_options = false and renders NO [data-qa-opt] groups, so `sel` is [].
      The length test below then compares 0 against a variant whose options are
      ["Default Title"] (length 1), never matches, and refreshButton() leaves
      "Select options" DISABLED forever. The shopper gets a QUICK ADD bar that
      opens a panel with nothing to choose and nothing to press — and because
      quick add also hides <product-form>, that card has no way to add at all.

      That is literally the complaint we are here to fix:
      Astrid, 6 Aug: "You also CAN NOT add to bag with choosing color and size,
      so the functionality isn't there anyway".

      No options to resolve means there is nothing to disambiguate, so take the
      product's own single variant — still an EXPLICIT id read from this card's
      embedded matrix, never from a page-wide variant input, which is the rule
      that keeps us clear of the wrong-product-added bug.
    */
    if (sel.length === 0) {
      var all = variantsOf(root);
      return all.find(function (v) { return v.available; }) || all[0] || null;
    }

    if (sel.some(function (v) { return v === null; })) return null;
    return variantsOf(root).find(function (v) {
      return v.options.length === sel.length && v.options.every(function (o, i) { return o === sel[i]; });
    }) || null;
  }

  // Grey out values that cannot combine with what is already chosen.
  function refreshAvailability(root) {
    var sel = selection(root);
    root.querySelectorAll('[data-qa-chip]').forEach(function (chip) {
      var i = parseInt(chip.dataset.optIndex, 10);
      var probe = sel.slice();
      probe[i] = chip.dataset.value;
      var reachable = variantsOf(root).some(function (v) {
        return v.available && probe.every(function (want, k) { return want === null || v.options[k] === want; });
      });
      chip.disabled = !reachable;
      chip.classList.toggle('is-unavailable', !reachable);
    });
  }

  function refreshButton(root) {
    var add = root.querySelector('[data-qa-add]');
    var label = root.querySelector('[data-qa-add-label]');
    var v = match(root);
    if (!v) {
      add.disabled = true;
      label.textContent = 'Select options';
    } else if (!v.available) {
      add.disabled = true;
      label.textContent = 'Sold out';
    } else {
      add.disabled = false;
      label.textContent = 'Add to bag — ' + MONEY(v.price);
    }
  }

  function openPanel(root, open) {
    var panel = root.querySelector('[data-qa-panel]');
    var opener = root.querySelector('[data-qa-open]');
    panel.hidden = !open;
    opener.setAttribute('aria-expanded', open ? 'true' : 'false');
    root.classList.toggle('is-open', open);
    if (open) {
      // The panel is capped at the height of the product image and scrolls
      // inside it when a product has enough options to overflow. Reopening must
      // start at the top, not wherever it was last left.
      panel.scrollTop = 0;
      // If an option has only one possible value, choose it for them.
      root.querySelectorAll('[data-qa-opt]').forEach(function (group) {
        var chips = group.querySelectorAll('[data-qa-chip]');
        if (chips.length === 1 && chips[0].getAttribute('aria-pressed') !== 'true') {
          chips[0].setAttribute('aria-pressed', 'true');
        }
      });
      refreshAvailability(root);
      refreshButton(root);
    }
  }

  function addToCart(root) {
    var v = match(root);
    if (!v) return;
    var add = root.querySelector('[data-qa-add]');
    var label = root.querySelector('[data-qa-add-label]');
    var status = root.querySelector('[data-qa-status]');
    var drawer = document.querySelector('cart-drawer');

    add.disabled = true;
    label.textContent = 'Adding…';
    status.textContent = '';

    var body = { items: [{ id: v.id, quantity: 1 }] };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.j && res.j.description ? res.j.description : 'Could not add to bag');
        label.textContent = 'Added';
        if (drawer && typeof drawer.renderContents === 'function' && res.j.sections) {
          drawer.renderContents(res.j);
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        setTimeout(function () { openPanel(root, false); refreshButton(root); }, 900);
      })
      .catch(function (err) {
        status.textContent = err.message || 'Could not add to bag';
        add.disabled = false;
        refreshButton(root);
      });
  }

  // Bind directly to each widget. Document-level delegation is NOT usable here:
  // <product-form> swallows click events, so trusted clicks never bubbled to document.
  function bind(root) {
    if (root._agntcBound) return;
    root._agntcBound = true;

    var opener = root.querySelector('[data-qa-open]');
    if (opener) {
      opener.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var willOpen = root.querySelector('[data-qa-panel]').hidden;
        document.querySelectorAll('[data-agntc-qa].is-open').forEach(function (other) {
          if (other !== root) openPanel(other, false);
        });
        openPanel(root, willOpen);
      });
    }

    var closer = root.querySelector('[data-qa-close]');
    if (closer) {
      closer.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation(); openPanel(root, false);
      });
    }

    root.querySelectorAll('[data-qa-chip]').forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (chip.disabled) return;
        var idx = chip.dataset.optIndex;
        root.querySelectorAll('[data-qa-chip][data-opt-index="' + idx + '"]').forEach(function (c) {
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        refreshAvailability(root);
        refreshButton(root);
      });
    });

    var add = root.querySelector('[data-qa-add]');
    if (add) {
      add.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (!add.disabled) addToCart(root);
      });
    }
  }

  /*
    Quick view (the magnifier).

    2026-08-06: the magnifier now sits as a small circular control over the card
    image, so it is no longer inside .card_product-add-to-cart — and that is the
    ONLY place the theme looks for it. global.js:1312 does:

        document.querySelectorAll(".preview-card .card_product-add-to-cart .quick-view-btn")

    ...so on quick-add cards the theme has never bound it; the modal simply never
    opened and the anchor navigated to the PDP instead. Re-bound here.

    Fail-open by design: if the theme's global is absent (script order, a future
    refactor, a JS error earlier in global.js) we do NOT preventDefault and the
    anchor just follows its href — which is precisely the current behaviour, so
    this can only ever improve on it, never regress it.

    Hooked on .agntc-qa-view rather than .quick-view-btn so this can never
    double-bind with global.js on cards that still render the magnifier in the
    old position (every non-quick-add template).
  */
  function bindQuickView(link) {
    if (link._agntcQvBound) return;
    link._agntcQvBound = true;

    link.addEventListener('click', function (e) {
      var openModal = window.clickPreviewProductPopupFetch;
      if (typeof openModal !== 'function') return;
      e.preventDefault();
      // Deliberately NOT stopPropagation: the click still needs to reach the
      // document click-away listener below so any open option panel closes
      // behind the modal.
      openModal(link.getAttribute('href'));
    });
  }

  function bindAll(scope) {
    var el = scope || document;
    el.querySelectorAll('[data-agntc-qa]').forEach(bind);
    el.querySelectorAll('.agntc-qa-view').forEach(bindQuickView);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindAll(); });
  } else {
    bindAll();
  }

  // The theme editor re-renders a section without ever firing DOMContentLoaded
  // again, so the readyState check above is not enough on its own.
  document.addEventListener('shopify:section:load', function (e) {
    bindAll(e.target || document);
  });

  // Cards can be re-rendered by the slider or by section reloads in the theme editor.
  if (window.MutationObserver) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var n = muts[i].addedNodes[j];
          if (n.nodeType !== 1) continue;
          // querySelectorAll never returns the node itself, so an added node that
          // IS a widget root or IS the magnifier has to be matched separately.
          if (n.matches && n.matches('[data-agntc-qa]')) bind(n);
          if (n.matches && n.matches('.agntc-qa-view')) bindQuickView(n);
          bindAll(n);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Click-away and Escape close any open panel.
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-agntc-qa]')) return;
    document.querySelectorAll('[data-agntc-qa].is-open').forEach(function (r) { openPanel(r, false); });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-agntc-qa].is-open').forEach(function (r) {
      openPanel(r, false);
      var o = r.querySelector('[data-qa-open]');
      if (o) o.focus();
    });
  });
})();
